import { type FC, useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import type { UserFilters, UserStatus, KYCStatus } from '@/types/user'
import { Filter } from 'lucide-react'

interface UsersFilterDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    currentFilters: UserFilters
    onApplyFilters: (filters: UserFilters) => void
}

export const UsersFilterDialog: FC<UsersFilterDialogProps> = ({
    open,
    onOpenChange,
    currentFilters,
    onApplyFilters,
}) => {
    const [filters, setFilters] = useState<UserFilters>(currentFilters)

    const handleApply = () => {
        onApplyFilters(filters)
        onOpenChange(false)
    }

    const handleReset = () => {
        const resetFilters: UserFilters = {
            page: 1,
            limit: 20,
        }
        setFilters(resetFilters)
        onApplyFilters(resetFilters)
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <div className="rounded-full bg-blue-100 p-2">
                            <Filter className="h-4 w-4 text-blue-600" />
                        </div>
                        <DialogTitle>Filter Users</DialogTitle>
                    </div>
                    <DialogDescription>
                        Apply filters to narrow down the user list
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Search */}
                    <div className="space-y-2">
                        <Label htmlFor="search">Search</Label>
                        <Input
                            id="search"
                            placeholder="Search by user ID, name, email, or wallet..."
                            value={filters.search || ''}
                            onChange={e =>
                                setFilters({
                                    ...filters,
                                    search: e.target.value || undefined,
                                })
                            }
                        />
                    </div>

                    {/* Status */}
                    <div className="space-y-2">
                        <Label htmlFor="status">Status</Label>
                        <Select
                            value={(filters.status as string) || 'all'}
                            onValueChange={(value: string) =>
                                setFilters({
                                    ...filters,
                                    status:
                                        value === 'all'
                                            ? undefined
                                            : (value as UserStatus),
                                })
                            }
                        >
                            <SelectTrigger id="status">
                                <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Status</SelectItem>
                                <SelectItem value="ACTIVE">Active</SelectItem>
                                <SelectItem value="FLAGGED">Flagged</SelectItem>
                                <SelectItem value="PENDING">Pending</SelectItem>
                                <SelectItem value="DELETED">Deleted</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* KYC Status */}
                    <div className="space-y-2">
                        <Label htmlFor="kycStatus">KYC Status</Label>
                        <Select
                            value={(filters.kycStatus as string) || 'all'}
                            onValueChange={(value: string) =>
                                setFilters({
                                    ...filters,
                                    kycStatus:
                                        value === 'all'
                                            ? undefined
                                            : (value as KYCStatus),
                                })
                            }
                        >
                            <SelectTrigger id="kycStatus">
                                <SelectValue placeholder="Select KYC status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    All KYC Status
                                </SelectItem>
                                <SelectItem value="NOT_VERIFIED">
                                    Not Verified
                                </SelectItem>
                                <SelectItem value="PENDING">Pending</SelectItem>
                                <SelectItem value="APPROVED">
                                    Approved
                                </SelectItem>
                                <SelectItem value="REJECTED">
                                    Rejected
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Email Verified */}
                    <div className="space-y-2">
                        <Label htmlFor="emailVerified">Email Verified</Label>
                        <Select
                            value={
                                filters.emailVerified === undefined
                                    ? 'all'
                                    : String(filters.emailVerified)
                            }
                            onValueChange={(value: string) =>
                                setFilters({
                                    ...filters,
                                    emailVerified:
                                        value === 'all'
                                            ? undefined
                                            : value === 'true',
                                })
                            }
                        >
                            <SelectTrigger id="emailVerified">
                                <SelectValue placeholder="Select email status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="true">Verified</SelectItem>
                                <SelectItem value="false">
                                    Not Verified
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Country */}
                    <div className="space-y-2">
                        <Label htmlFor="country">Country</Label>
                        <Input
                            id="country"
                            placeholder="Enter country name..."
                            value={filters.country || ''}
                            onChange={e =>
                                setFilters({
                                    ...filters,
                                    country: e.target.value || undefined,
                                })
                            }
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleReset}>
                        Reset Filters
                    </Button>
                    <Button onClick={handleApply}>Apply Filters</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
